import {
  getEligibleClassCodesForCOA,
  getIneligibleClassCodesForCOA,
  getEligibleDPSCForCOA,
  getIneligibleDPSCForPhotoID,
  getEligibleRecordTypesForCOA,
} from './eligibilityCodes'
import { REFERENCE_TABLE } from '@/constants/ReferenceTables'
import { useConfigStore } from '@/stores/config'
import { useUserLogin } from '@/stores/userLogin'
import { formatDate } from '@/util/formatters'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(isSameOrBefore)

/**
 * Parse class code string into array of individual codes
 * @param {string} classCode - e.g., "Y *" or "ABC"
 * @returns {string[]} Array of individual class codes
 */
export const parseClassCodes = (classCode) => {
  if (!classCode) return []
  return classCode
    .trim()
    .split('')
    .filter((code) => code !== '' && code !== ' ')
}

/**
 * Parse dlType string (up to 6 chars) into array of 2-character ID type codes
 * @param {string} dlType - e.g., "DL", "DLID", "DLIDLP"
 * @returns {string[]} Array of 2-char ID type codes
 */
export const parseDLTypeCodes = (dlType) => {
  if (!dlType) return []
  const codes = []
  for (let i = 0; i < dlType.length; i += 2) {
    const code = dlType.substring(i, i + 2)
    if (code) codes.push(code)
  }
  return codes
}

/**
 * Check if class codes are eligible for COA
 * @param {string[]} classCodes - Array of individual class codes
 * @param {Object} config - Driver config from API
 * @returns {boolean} True if eligible
 */
export const areClassCodesEligible = (classCodes, config) => {
  if (classCodes.length === 0) return true

  const INELIGIBLE_CLASS_CODES_FOR_COA = getIneligibleClassCodesForCOA(config)
  const ELIGIBLE_CLASS_CODES_FOR_COA = getEligibleClassCodesForCOA(config)

  // If ANY code is ineligible, return false
  const hasIneligible = classCodes.some((code) => INELIGIBLE_CLASS_CODES_FOR_COA.has(code))
  if (hasIneligible) return false

  // Must have at least one eligible code
  const hasEligible = classCodes.some((code) => ELIGIBLE_CLASS_CODES_FOR_COA.has(code))
  return hasEligible
}

/**
 * Check if DPSC codes are eligible for Driver's License COA
 * @param {string[]} dpscCodes - Array of 2-char DPSC codes
 * @param {Object} config - Driver config from API
 * @returns {boolean} True if eligible
 */
export const areDPSCCodesEligibleForDL = (dpscCodes, config) => {
  if (!dpscCodes || dpscCodes.length === 0) return true

  const ELIGIBLE_DPSC_FOR_COA = getEligibleDPSCForCOA(config)

  // ALL DPSC codes must be in the eligible list
  return dpscCodes.every((code) => ELIGIBLE_DPSC_FOR_COA.has(code))
}

/**
 * Check if DPSC codes are eligible for Photo ID COA
 * @param {string[]} dpscCodes - Array of 2-char DPSC codes
 * @param {Object} config - Driver config from API
 * @returns {boolean} True if eligible (no FR or NP)
 */
export const areDPSCCodesEligibleForPhotoID = (dpscCodes, config) => {
  if (!dpscCodes || dpscCodes.length === 0) return true

  const INELIGIBLE_DPSC_FOR_PHOTO_ID = getIneligibleDPSCForPhotoID(config)

  // Must NOT contain FR or NP
  return !dpscCodes.some((code) => INELIGIBLE_DPSC_FOR_PHOTO_ID.has(code))
}

/**
 * Check if record type is eligible for COA
 * @param {string} recordType - Single record type code
 * @param {Object} config - Driver config from API
 * @returns {boolean} True if eligible
 */
export const isRecordTypeEligible = (recordType, config) => {
  if (!recordType) return false

  const ELIGIBLE_RECORD_TYPES_FOR_COA = getEligibleRecordTypesForCOA(config)

  return ELIGIBLE_RECORD_TYPES_FOR_COA.has(recordType.trim())
}

/**
 * Determine if ID type includes Driver's License or Learner Permit
 * @param {string} dlType - ID type string up to 6 chars (e.g., "DL", "DLID", "DLIDLP")
 * @returns {boolean} True if DL or LP is present
 */
export const isDriverLicense = (dlType) => {
  if (!dlType) return false
  const codes = parseDLTypeCodes(dlType)
  return codes.includes('DL')
}

/**
 * Determine if ID type includes Learner Permit
 * @param {string} dlType - ID type string up to 6 chars (e.g., "DL", "DLID", "DLIDLP")
 * @returns {boolean} True if LP is present
 */
export const isLearnerPermit = (dlType) => {
  if (!dlType) return false
  const codes = parseDLTypeCodes(dlType)
  return codes.includes('LP')
}

/**
 * Determine if ID type includes Photo ID
 * @param {string} dlType - ID type string up to 6 chars (e.g., "DL", "DLID", "DLIDLP")
 * @returns {boolean} True if ID is present
 */
export const isPhotoID = (dlType) => {
  if (!dlType) return false
  return parseDLTypeCodes(dlType).includes('ID')
}

/**
 * Get human-readable description for a dlType string by looking up each 2-char code,
 * adding "- Real ID" if necessary, and joining with " & "
 * @param {string} dlType - ID type string up to 6 chars (e.g., "DLIDLP")
 * @returns {string} e.g., "Driver License & Photo ID & Learner Permit"
 */
export const getDLTypeDescription = (dlType) => {
  if (!dlType) return ''
  const configStore = useConfigStore()
  const userLoginStore = useUserLogin()
  return parseDLTypeCodes(dlType)
    .map((code) => {
      const value = configStore.getReferenceValue(REFERENCE_TABLE.ID_TYPE, code)
      if (value) {
        // If value has a real id & is not a learner permit, add "-Real ID" to the end of specified display string
        // RealIdIndRaw = 1 is real driver license, 3 is real photo id
        if (userLoginStore?.loginResponse?.realIdInd == 'Y') {
          if (userLoginStore?.loginResponse?.realIdIndRaw == 1) {
            return code == 'DL' ? `${value} - Real ID` : value
          } else {
            return code == 'ID' ? `${value} - Real ID` : value
          }
        }
        return value
      }
    })
    .filter(Boolean)
    .join(' & ')
}

/**
 * Determine the appropriate COA service type based on dlType
 * @param {string} dlType - ID type string up to 6 chars (e.g., "DL", "DLID", "DLIDLP")
 * @returns {string} Service type (IDCOA or DLCOA)
 */
export const getCoaServiceType = (dlType) => {
  if (!dlType) return 'DLCOA'
  const codes = parseDLTypeCodes(dlType)
  if (codes.includes('ID') && !codes.includes('DL') && !codes.includes('LP')) return 'IDCOA'
  return 'DLCOA'
}

/**
 * Check if immigration status is eligible for renewal.
 * Must be 'P' (permanent resident), 'N' (non-immigrant), or blank/null.
 * @param {string|null} immigrationStatus - Immigration status from loginResponse
 * @returns {boolean} True if eligible
 */
export const isEligibleImmigrationStatus = (immigrationStatus) => {
  const status = immigrationStatus?.trim() ?? null
  return status === 'P' || status === 'N' || status === '' || status === null
}

/**
 * Build the scheduling banner table data structure for Driver Test Dashboard
 * @param {Object} data - User data from loginResponse
 * @returns {Array[]} Two-row table array: headers and values
 */
export const getSchedulingTableData = (data) => {
  if (!data) {
    return [
      ['Name', 'Identification Number', 'Date of Birth'],
      ['', '', ''],
    ]
  }

  const name = [data.firstName, data.lastName].filter(Boolean).join(' ')
  const dob = formatDate(data.dateOfBirth)

  return [
    ['Name', 'Identification Number', 'Date of Birth'],
    [name || '-', data.olNum || '-', dob || '-'],
  ]
}

/**
 * Build the adding services table data structure
 * @param {Object} data - User data from loginResponse
 * @returns {Array[]} Two-row table array: headers and values
 */
export const getAddingServicesTableData = (data) => {
  if (!data) {
    return [
      ['Identification Number', 'Date of Birth', 'Identification Type'],
      ['', '', ''],
    ]
  }

  const dob = formatDate(data.dateOfBirth)

  return [
    ['Identification Number', 'Date of Birth', 'Identification Type'],
    [data.olNum || '-', dob || '-', getDLTypeDescription(data.dlType)],
  ]
}

/**
 * Build the adding services table data structure for Vehicle Services (VS)
 * @param {Object} data - User data from loginResponse
 * @returns {Array[]} Two-row table array: headers and values
 */
export const getVSAddingServicesTableData = (data) => {
  if (!data) {
    return [
      ['Title Number', 'Plate Number'],
      ['', ''],
    ]
  }
  return [
    ['Title Number', 'Plate Number'],
    [data.plate.legacyTitleNum || '-', data.plate.plateNum || '-'],
  ]
}

/**
 * Validates a date string.
 * @param {string|null} dateStr - The date string to validate.
 * @returns {Date|null} - Returns the Date object if valid and >= 1800-01-01; otherwise returns null.
 */
export const validateDate = (dateString) => {
  // 1. Check if input is null or empty
  if (!dateString) return null
  const dateObj = dayjs(dateString)
  // Check if valid date
  if (!dateObj.isValid()) return null
  // Set minimum bound: 01/01/1800
  const minDate = dayjs('1800-01-01')
  // Return null if before 01/01/1800
  if (dateObj.isSameOrBefore(minDate)) {
    return null
  }
  return dateObj.format('YYYY-MM-DD')
}

/**
 * Validate that the DL/ID expiration date falls within the early renewal window.
 * The expiration date must be no more than N months in the future, where N comes
 * from the EARLY_RENEWAL_LIMIT reference table (defaults to 6 months).
 * There is no lower bound — expired licenses are still eligible to renew.
 * @param {string} currentExpDate - Expiration date string from loginResponse
 * @param {number} earlyRenewalMonths - Number of months before expiration renewal is allowed
 * @returns {{ eligible: boolean, expDate: dayjs.Dayjs }}
 */
export const validateRenewalWindow = (currentExpDate, earlyRenewalMonths) => {
  // Validate earlyRenewalMonths
  const months = Number(earlyRenewalMonths)
  if (Number.isNaN(months) || months < 0) {
    return { eligible: false, expDate: null }
  }

  // Validate currentExpDate
  if (!currentExpDate) {
    return { eligible: false, expDate: null }
  }

  const expDate = dayjs(currentExpDate)
  const today = dayjs()
  // Expiration date must be no more than N months in the future (no lower bound — past expirations are still eligible)
  const eligible = expDate.isValid() && !expDate.isAfter(today.add(earlyRenewalMonths, 'month'), 'day')
  return { eligible, expDate }
}
/**
 * Determines if the DL/ID expiry date is after the grandfathered date,
 * which dictates whether a PDPS (Problem Driver Pointer System) call is required.
 * @param {string} expiryDate
 * @param {string} pdpsGrandfatheredDate
 * @returns {boolean} Returns true if the expiry date is after the grandfathered date, false otherwise.
 */
export const isExpirationAfterGrandfatheredDate = (expireDate, grandfatheredDate) => {
  if (!expireDate || !grandfatheredDate) return false
  const exp = expireDate instanceof Date ? expireDate : new Date(expireDate)
  const gf = grandfatheredDate instanceof Date ? grandfatheredDate : new Date(grandfatheredDate)
  if (Number.isNaN(exp.getTime()) || Number.isNaN(gf.getTime())) return false
  return exp > gf
}
